import type { Task } from "~skillBuilder/utils/types";

import { image, tip, title } from "../../contentBlocks";

export const TspStudentEngagement = {
  name: "Student Engagement",
  uniqueName: "tsp-student-engagement",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.student-engagement/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Student Engagement", durationTimeText: "10 min" })}
<p>Your presence as a substitute teacher dictates the energy of the room. This guide provides actionable strategies to keep students focused, motivated, and learning from the first bell to the last.</p>
<div align="center">
<div style="max-width:300px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1775230632/skillbuilderAssets/optimized/Student_Centered.jpg", alt: "Illustration of students and learning in the classroom." })}
</div>
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.student-engagement/active-presence",
          label: "Active Presence & Monitoring",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>The goal is to model the behavior you expect from students.</p>
<ul style="margin-left: 16px;">
  <li>👏 <strong>Model Engagement:</strong> Stay off personal devices and avoid personal reading/music. Students model your behavior; if you are present, they are more likely to be.</li>
  <li>🔁 <strong>Circulate the Room:</strong> Do not remain at the desk. Continuously scan the room or walk through rows while students work to provide support and discourage off-task behavior.</li>
  <li>⭐ <strong>Positive Reinforcement:</strong> Focus on acknowledging students who are on task rather than singling out those who aren&apos;t.</li>
  <li>🗣️ <strong>Vocal Resets:</strong> To get attention, use a calm whisper or a call-and-response technique. Never use yelling or physical contact to gain attention.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.student-engagement/rapport",
          label: "Building Instant Rapport",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>🏷️ <strong>The Power of Names:</strong> Learn and use student names immediately. It builds accountability and respect.</li>
  <li>⚡ <strong>High Energy:</strong> Showcase enthusiasm for the material. Even if it&rsquo;s a challenging subject, your positive mental attitude is contagious.</li>
  <li>⭐ <strong>Validate Effort:</strong> Use specific praise (e.g., &ldquo;I appreciate how you phrased that answer&rdquo;) to make students feel seen and respected.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.student-engagement/toolkit",
          label: "The Engagement Toolkit",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Use these tools to maintain momentum throughout the lesson.</p>
<ul style="margin-left: 16px;">
  <li>🧰 <strong>Tech & Tools:</strong> Use available classroom technology and writing tools to make the lesson visual and interactive.</li>
  <li>🤝 <strong>Collaboration:</strong> Encourage peer-to-peer discussion or group &ldquo;brain teasers&rdquo; to break up independent work.</li>
  <li>🏃🏿‍♀️ <strong>Strategic Breaks:</strong> Insert 2-minute &ldquo;Movement Breaks&rdquo; (stretching or a quick standing activity) if you notice focus dipping.</li>
  <li>🔁 <strong>Review & Reflect:</strong> Start with a quick review of previous material and end with a &ldquo;3-2-1 Reflection&rdquo; (3 things learned, 2 questions, 1 big takeaway).</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.student-engagement/developmental",
          label: "Developmental Strategies",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Adapt your approach based on the age group you are teaching.</p>
<ul style="margin-left: 16px;">
  <li>🎯 <strong>Elementary:</strong> Prioritize structured transitions, frequent movement, and clear, repetitive instructions.</li>
  <li>🎯 <strong>Middle/High School:</strong> Focus on relevance, autonomy, and professional respect. Give them &ldquo;why&rdquo; the work matters.</li>
</ul>
${tip({ text: "For smooth transitions, always give a &ldquo;2-minute warning&rdquo; before switching activities to minimize disruptions." })}
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.student-engagement/wrap-up",
          label: "Wrap-Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>🎟️ <strong>Exit Tickets:</strong> Use a quick check-for-understanding before students leave.</li>
  <li>📝 <strong>Teacher Notes:</strong> Leave a concise summary for the permanent teacher regarding student behavior, lesson progress, and any outstanding wins or challenges.</li>
  <li>⭐ <strong>End Positively:</strong> Dismiss the class with a word of encouragement to ensure they leave with a positive view of the day&rsquo;s learning.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 7
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.student-engagement/strategy-action",
          label: "Strategy in Action",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>Situation:</strong></p>
<p>It&rsquo;s 1:30 PM on a Thursday. You are subbing for a Life Science class. The students have just come from lunch, the energy is erratic, and the lesson plan is a 30-minute independent reading packet on cell structures.</p>
<p><strong>The Move:</strong> You stand by the door, greeting students by name as they walk in (checking the seating chart or asking them).</p>
<p>After briefly setting expectations and providing directions, you set the students to work and you circulate the room. As students start their packets, you walk the rows. You notice a student, Maya, has her head down. You walk over to the student and whisper, &ldquo;I saw your drawing on the cover, Maya&mdash;really detailed. Can you show me that same effort in the first three definitions?&rdquo; As Maya gets back on task, the &ldquo;slump&rdquo; hits the rest of the classroom. Volume is rising in the classroom and focus is dipping. You notice the vibe and know you need to take action fast or risk falling behind in the assigned work and the day&rsquo;s schedule.</p>
<p><strong>Potential Solution:</strong></p>
<p>It&rsquo;s time for a strategic break. You address the students from the front of the class &ldquo;Alright everyone, eyes on me. We&rsquo;re going to take a 2-minute movement break! Everyone stand up, stretch your arms to the ceiling, and give a quick (and gentle!) high-five to your neighbor.&rdquo;</p>
<p><strong>The Reset:</strong> After 2 minutes, you use a vocal reset (a calm call-and-response like &ldquo;If you can hear me, clap once&rdquo;) to capture their attention and bring them back to the task.</p>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
